"use strict";
import { useQuery } from "@tanstack/react-query";
import { useAccount } from "hooks/useAccount";
import { getBscSubgraphs, TICK_SPACING_TO_FEE } from "constants/bscContracts";
import {
  fetchV2PoolMetadataOnChain,
  fetchV3PoolMetadataOnChain,
  shouldUseOnChainV2
} from "features/routing/api/bscOnChainPools";
import { fetchFromSubgraph } from "utils/fetchSubgraph";
import { UniverseChainId } from "features/chains/types";
import { useOnChainGaugeList } from "./voting/useOnChainGaugeList";
async function fetchDataFromSubgraph(query, endpoint, variables) {
  try {
    const data = await fetchFromSubgraph(endpoint, {
      query,
      variables,
      requestId: "useGaugeData"
    });
    return data;
  } catch (error) {
    console.error("Subgraph query failed:", error);
    return null;
  }
}
const GET_V2_POOLS_BY_IDS = `
  query GetV2PoolsByIds($ids: [String!]!) {
    pairs(where: { id_in: $ids }) {
      id
      token0 {
        id
        symbol
        name
        decimals
      }
      token1 {
        id
        symbol
        name
        decimals
      }
      reserveUSD
      volumeUSD
      stable
    }
  }
`;
const GET_V3_POOLS_BY_IDS = `
  query GetV3PoolsByIds($ids: [String!]!) {
    pools(where: { id_in: $ids }) {
      id
      token0 {
        id
        symbol
        name
        decimals
      }
      token1 {
        id
        symbol
        name
        decimals
      }
      tickSpacing
      totalValueLockedUSD
      totalValueLockedToken0
      totalValueLockedToken1
      volumeUSD
    }
  }
`;
const GAUGES_QUERY = `
  query GetGauges($user: String!) {
    gauges {
      id
      pool
    }
    
    gaugeStakes(where: { user: $user }) {
      id
      user
      gauge {
        id
        pool
      }
      amount
    }
    
    rewardClaims(where: { user: $user }) {
      id
      user
      amount
      token
    }
  }
`;
export function calculateGaugeFeePercentage(gauge) {
  if (gauge.poolType === "V2") {
    return "0.30";
  } else if (gauge.tickSpacing) {
    const tickSpacing = parseInt(gauge.tickSpacing);
    const feeBasisPoints = TICK_SPACING_TO_FEE[tickSpacing] || 3e3;
    return (feeBasisPoints / 1e4).toFixed(2);
  }
  return "0.30";
}
export function useGaugesData(chainOverride) {
  const { chainId: accountChainId } = useAccount();
  const chainId = chainOverride ?? accountChainId ?? UniverseChainId.Mainnet;
  const onChainGaugeList = useOnChainGaugeList(chainId);
  const gauges = onChainGaugeList.gauges.map((g) => ({
    id: g.id,
    pool: g.pool
  }));
  const poolMappings = onChainGaugeList.gauges.map((g) => ({
    id: `${g.pool}-${g.id}`,
    pool: g.pool,
    gauge: g.id
  }));
  return {
    data: { gauges, poolMappings },
    isLoading: onChainGaugeList.isLoading,
    error: onChainGaugeList.error,
    isError: !!onChainGaugeList.error
  };
}
export function useUserGaugeData() {
  const { address, chainId } = useAccount();
  return useQuery({
    queryKey: ["userGauges", address, chainId],
    queryFn: async () => {
      const subgraphs = chainId ? getBscSubgraphs(chainId) : null;
      if (!subgraphs?.gauges || !address) {
        throw new Error("Missing subgraph URL or user address");
      }
      const gaugeSubgraph = subgraphs.gauges;
      const data = await fetchFromSubgraph(gaugeSubgraph, {
        query: GAUGES_QUERY,
        variables: { user: address.toLowerCase() },
        requestId: "useGaugeData.userGauges"
      });
      return {
        gauges: data?.gauges || [],
        stakes: data?.gaugeStakes || [],
        claims: data?.rewardClaims || []
      };
    },
    enabled: Boolean(chainId && address),
    staleTime: 3e4,
    refetchInterval: 6e4
  });
}
export function useGaugeForPool(poolAddress, chainOverride) {
  const { data: gaugeData, isLoading } = useGaugesData(chainOverride);
  const gauge = gaugeData?.poolMappings?.find((mapping) => {
    const mappingPoolAddress = mapping.pool.toLowerCase();
    const targetPoolAddress = poolAddress?.toLowerCase();
    return mappingPoolAddress === targetPoolAddress;
  });
  return {
    gauge: gauge ? { id: gauge.gauge } : null,
    poolMapping: gauge,
    isLoading,
    hasGauge: Boolean(gauge)
  };
}
export function useAvailableGauges(enabled = true, chainOverride) {
  const { chainId: accountChainId } = useAccount();
  const chainId = chainOverride ?? accountChainId ?? UniverseChainId.Mainnet;
  const onChainGaugeList = useOnChainGaugeList(chainId, enabled);
  const poolMetadataQuery = useQuery({
    queryKey: ["available-gauges-pool-metadata", chainId, onChainGaugeList.gauges.map((g) => g.pool).join(",")],
    queryFn: async () => {
      if (onChainGaugeList.gauges.length === 0) {
        return [];
      }
      const v2PoolIds = onChainGaugeList.gauges.filter((g) => g.poolType === "V2").map((g) => g.pool.toLowerCase());
      const v3PoolIds = onChainGaugeList.gauges.filter((g) => g.poolType !== "V2").map((g) => g.pool.toLowerCase());
      let v2Pools = [];
      let v3Pools = [];
      if (shouldUseOnChainV2(chainId)) {
        const [v2Meta, v3Meta] = await Promise.all([
          fetchV2PoolMetadataOnChain(v2PoolIds, chainId),
          fetchV3PoolMetadataOnChain(v3PoolIds, chainId)
        ]);
        v2Pools = v2Meta;
        v3Pools = v3Meta;
      } else {
        const subgraphs = getBscSubgraphs(chainId);
        const allPoolIds = [...v2PoolIds, ...v3PoolIds];
        const v2Data = await fetchDataFromSubgraph(
          GET_V2_POOLS_BY_IDS,
          subgraphs.v2,
          { ids: allPoolIds }
        );
        v2Pools = v2Data?.pairs || [];
        const v3Data = await fetchDataFromSubgraph(
          GET_V3_POOLS_BY_IDS,
          subgraphs.v3,
          { ids: allPoolIds }
        );
        v3Pools = v3Data?.pools || [];
      }
      const v2PoolMap = /* @__PURE__ */ new Map();
      const v3PoolMap = /* @__PURE__ */ new Map();
      v2Pools.forEach((pool) => v2PoolMap.set(pool.id.toLowerCase(), pool));
      v3Pools.forEach((pool) => v3PoolMap.set(pool.id.toLowerCase(), {
        ...pool,
        reserveUSD: pool.totalValueLockedUSD
      }));
      const enhancedGauges = onChainGaugeList.gauges.map((gauge) => {
        const poolId = gauge.pool.toLowerCase();
        const v2PoolData = v2PoolMap.get(poolId);
        const v3PoolData = v3PoolMap.get(poolId);
        const poolData = gauge.poolType === "V2" ? v2PoolData : v3PoolData;
        const baseGauge = {
          id: gauge.id,
          pool: gauge.pool,
          poolType: gauge.poolType,
          isAlive: gauge.isAlive,
          totalWeight: "0",
          // Not available on-chain in base hook
          emissions: "0",
          bribeContract: gauge.bribeContract,
          feeContract: gauge.feeContract,
          creator: "",
          factory: "",
          createdAtTimestamp: ""
        };
        if (poolData) {
          return {
            ...baseGauge,
            token0: poolData.token0,
            token1: poolData.token1,
            tickSpacing: poolData.tickSpacing,
            reserveUSD: poolData.reserveUSD,
            volumeUSD: poolData.volumeUSD,
            votes: "0",
            stable: poolData.stable,
            totalValueLockedUSD: poolData.totalValueLockedUSD ?? poolData.reserveUSD,
            totalValueLockedToken0: poolData.totalValueLockedToken0 ?? poolData.reserve0,
            totalValueLockedToken1: poolData.totalValueLockedToken1 ?? poolData.reserve1
          };
        }
        return baseGauge;
      });
      return enhancedGauges;
    },
    enabled: enabled && !onChainGaugeList.isLoading && onChainGaugeList.gauges.length > 0,
    staleTime: 5 * 6e4,
    // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
  const isLoading = onChainGaugeList.isLoading || poolMetadataQuery.isLoading;
  const error = onChainGaugeList.error || poolMetadataQuery.error;
  return {
    data: poolMetadataQuery.data ?? [],
    isLoading,
    isError: !!error,
    error,
    // Add other UseQueryResult properties as needed
    refetch: poolMetadataQuery.refetch,
    isFetching: poolMetadataQuery.isFetching,
    isSuccess: !isLoading && !error
  };
}
export function calculatePoolIncentives(gaugePool) {
  let totalIncentives = 0;
  let totalEmissions = 0;
  if (gaugePool.emissions && gaugePool.emissions !== "0") {
    try {
      totalEmissions = parseFloat((BigInt(gaugePool.emissions) / BigInt(10 ** 18)).toString());
    } catch (error) {
      console.warn("Failed to parse emissions:", gaugePool.emissions, error);
    }
  }
  if (gaugePool.rewardDistributions) {
    gaugePool.rewardDistributions.forEach((distribution) => {
      if (distribution.amount && distribution.amount !== "0") {
        try {
          const amount = parseFloat((BigInt(distribution.amount) / BigInt(10 ** 18)).toString());
          totalIncentives += amount;
        } catch (error) {
          console.warn("Failed to parse reward distribution:", distribution.amount, error);
        }
      }
    });
  }
  const totalRewards = totalIncentives + totalEmissions;
  const incentivesFormatted = totalRewards > 0 ? `${totalRewards.toLocaleString(void 0, { maximumFractionDigits: 0 })} TOPAZ` : "-";
  return {
    totalIncentives,
    totalEmissions,
    totalRewards,
    incentivesFormatted
  };
}
export function calculatePoolTradingFeesLastEpoch(gaugePool) {
  const currentTime = Date.now() / 1e3;
  const currentEpoch = Math.floor(currentTime / (7 * 24 * 60 * 60));
  const lastEpoch = currentEpoch - 1;
  let totalTradingFees = 0;
  const feeTokenMap = /* @__PURE__ */ new Map();
  if (gaugePool.rewardDistributions) {
    gaugePool.rewardDistributions.forEach((distribution) => {
      if (distribution.amount && distribution.amount !== "0") {
        const distributionEpoch = parseInt(distribution.epoch || "0");
        if (distributionEpoch === lastEpoch) {
          try {
            const amount = parseFloat((BigInt(distribution.amount) / BigInt(10 ** 18)).toString());
            let tokenSymbol = "UNKNOWN";
            let isTradingFee = false;
            if (distribution.token === "0x9edcde0257f2386ce177c3a7fcdd97787f0d841d") {
              tokenSymbol = "WETH";
              isTradingFee = true;
            } else if (distribution.token === "0x698f98ab816670692fa9dd8e1486ea0fd253e996") {
              tokenSymbol = "USDC";
              isTradingFee = true;
            } else if (distribution.token === "0x0000000000000000000000000000000000000000") {
              tokenSymbol = "ETH";
              isTradingFee = true;
            }
            if (isTradingFee) {
              totalTradingFees += amount;
              feeTokenMap.set(tokenSymbol, (feeTokenMap.get(tokenSymbol) || 0) + amount);
            }
          } catch (error) {
            console.warn("Failed to parse trading fee distribution:", distribution.amount, error);
          }
        }
      }
    });
  }
  const feeBreakdown = Array.from(feeTokenMap.entries()).map(([symbol, amount]) => ({
    symbol,
    amount: amount < 1e3 ? amount.toFixed(2) : amount < 1e6 ? (amount / 1e3).toFixed(2) + "K" : (amount / 1e6).toFixed(2) + "M"
  }));
  const formattedTotal = totalTradingFees > 0 ? totalTradingFees < 1e3 ? totalTradingFees.toFixed(2) : totalTradingFees < 1e6 ? (totalTradingFees / 1e3).toFixed(2) + "K" : (totalTradingFees / 1e6).toFixed(2) + "M" : "-";
  return {
    totalTradingFees,
    feeBreakdown,
    formattedTotal
  };
}
const SECONDS_PER_WEEK = 7 * 24 * 60 * 60;
const WEEKS_PER_YEAR = 52;
export function calculatePoolLPAPR({
  rewardRatePerSecond,
  stakedTvlUSD,
  topazPriceUSD
}) {
  if (!rewardRatePerSecond || rewardRatePerSecond <= 0) {
    return 0;
  }
  if (!stakedTvlUSD || stakedTvlUSD <= 0) {
    return 0;
  }
  if (!topazPriceUSD || topazPriceUSD <= 0) {
    return 0;
  }
  const weeklyEmissionsTOPAZ = rewardRatePerSecond * SECONDS_PER_WEEK;
  const annualRewardsUSD = weeklyEmissionsTOPAZ * WEEKS_PER_YEAR * topazPriceUSD;
  const apr = annualRewardsUSD / stakedTvlUSD * 100;
  return Number.isFinite(apr) ? apr : 0;
}
export function calculatePoolVAPR(_gaugePool, _tvlUSD) {
  return "0.00";
}
export function calculateEstimatedRewards(gaugePool, userVotePercentage, userTotalVotingPower, topazPrice = 1) {
  const { totalRewards } = calculatePoolIncentives(gaugePool);
  if (totalRewards === 0) {
    return {
      rewardsTOPAZ: 0,
      rewardsUSD: 0,
      formattedUSD: "$0.00"
    };
  }
  const userVoteWeight = userVotePercentage / 100 * userTotalVotingPower;
  const totalPoolWeight = gaugePool.totalWeight ? parseFloat(gaugePool.totalWeight) : 0;
  if (totalPoolWeight === 0) {
    return {
      rewardsTOPAZ: 0,
      rewardsUSD: 0,
      formattedUSD: "$0.00"
    };
  }
  const userShareOfPool = userVoteWeight / totalPoolWeight;
  const estimatedRewardsTOPAZ = userShareOfPool * totalRewards;
  const estimatedRewardsUSD = estimatedRewardsTOPAZ * topazPrice;
  return {
    rewardsTOPAZ: estimatedRewardsTOPAZ,
    rewardsUSD: estimatedRewardsUSD,
    formattedUSD: estimatedRewardsUSD > 0 ? `~$${estimatedRewardsUSD.toFixed(2)}` : "$0.00"
  };
}
